import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { NotificationRecipient } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { useNotificationContext } from '../contexts/NotificationContext';

interface NotificationManagerProps {
  notifications: NotificationRecipient[];
  onChange: (notifications: NotificationRecipient[]) => void;
  readOnly?: boolean;
}

export const NotificationManager: React.FC<NotificationManagerProps> = ({
  notifications,
  onChange,
  readOnly = false,
}) => {
  const { showSuccess, showError } = useNotificationContext();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newNotification, setNewNotification] = useState<Partial<NotificationRecipient>>({
    channel: 'email',
    recipient: '',
    countryCode: '+1',
    enabled: true,
  });

  const handleAddNotification = () => {
    if (!newNotification.channel || !newNotification.recipient) {
      showError('Invalid Input', 'Please fill in all required fields');
      return;
    }

    // Basic validation
    if (newNotification.channel === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(newNotification.recipient)) {
        showError('Invalid Email', 'Please enter a valid email address');
        return;
      }
    } else if (newNotification.channel === 'sms') {
      const phonePattern = /^\d{10,15}$/;
      if (!phonePattern.test(newNotification.recipient)) {
        showError('Invalid Phone Number', 'Please enter a valid phone number (10-15 digits)');
        return;
      }
    }

    const notification: NotificationRecipient = {
      id: uuidv4(),
      channel: newNotification.channel as 'email' | 'sms',
      recipient: newNotification.recipient,
      countryCode: newNotification.channel === 'sms' ? newNotification.countryCode : undefined,
      enabled: true,
    };

    onChange([...notifications, notification]);
    setIsAddDialogOpen(false);
    setNewNotification({
      channel: 'email',
      recipient: '',
      countryCode: '+1',
      enabled: true,
    });

    // Show success feedback
    const channelText = newNotification.channel === 'email' ? 'Email' : 'SMS';
    const recipientText = newNotification.channel === 'sms' && newNotification.countryCode 
      ? `${newNotification.countryCode} ${newNotification.recipient}`
      : newNotification.recipient;
    
    showSuccess(
      'Notification Added', 
      `${channelText} notification added for ${recipientText}`
    );
  };

  const handleToggleNotification = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;

    const newEnabled = !notification.enabled;
    onChange(
      notifications.map((n) =>
        n.id === id ? { ...n, enabled: newEnabled } : n
      )
    );

    // Show success feedback
    const channelText = notification.channel === 'email' ? 'Email' : 'SMS';
    const recipientText = notification.channel === 'sms' && notification.countryCode 
      ? `${notification.countryCode} ${notification.recipient}`
      : notification.recipient;
    
    showSuccess(
      'Notification Updated', 
      `${channelText} notification ${newEnabled ? 'enabled' : 'disabled'} for ${recipientText}`
    );
  };

  const handleDeleteNotification = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;

    onChange(notifications.filter((n) => n.id !== id));

    // Show success feedback
    const channelText = notification.channel === 'email' ? 'Email' : 'SMS';
    const recipientText = notification.channel === 'sms' && notification.countryCode 
      ? `${notification.countryCode} ${notification.recipient}`
      : notification.recipient;
    
    showSuccess(
      'Notification Removed', 
      `${channelText} notification removed for ${recipientText}`
    );
  };

  const getNotificationIcon = (channel: 'email' | 'sms') => {
    return channel === 'email' ? <EmailIcon /> : <SmsIcon />;
  };

  const formatRecipient = (notification: NotificationRecipient) => {
    if (notification.channel === 'sms' && notification.countryCode) {
      return `${notification.countryCode} ${notification.recipient}`;
    }
    return notification.recipient;
  };

  return (
    <Box>
      <Stack spacing={2}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Notification Recipients</Typography>
          {!readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
            >
              Add Notification
            </Button>
          )}
        </Box>

        {/* Notification List */}
        {notifications.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" align="center">
                No notification recipients configured.
                {!readOnly && ' Click "Add Notification" to get started.'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1}>
            {notifications.map((notification) => (
              <Card key={notification.id} variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Channel Icon */}
                    <Box color={notification.channel === 'email' ? 'primary.main' : 'success.main'}>
                      {getNotificationIcon(notification.channel)}
                    </Box>

                    {/* Recipient Info */}
                    <Box flex={1}>
                      <Typography variant="body1">{formatRecipient(notification)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notification.channel === 'email' ? 'Email' : 'SMS'}
                      </Typography>
                    </Box>

                    {/* Status Chip */}
                    <Chip
                      label={notification.enabled ? 'Enabled' : 'Disabled'}
                      color={notification.enabled ? 'success' : 'default'}
                      size="small"
                    />

                    {/* Controls */}
                    {!readOnly && (
                      <>
                        <Switch
                          checked={notification.enabled}
                          onChange={() => handleToggleNotification(notification.id)}
                          color="primary"
                        />
                        <IconButton
                          onClick={() => handleDeleteNotification(notification.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Add Notification Dialog */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Notification Recipient</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Channel Selection */}
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={newNotification.channel || 'email'}
                label="Channel"
                onChange={(e: any) =>
                  setNewNotification({
                    ...newNotification,
                    channel: e.target.value as 'email' | 'sms',
                  })
                }
              >
                <MenuItem value="email">
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon /> Email
                  </Box>
                </MenuItem>
                <MenuItem value="sms">
                  <Box display="flex" alignItems="center" gap={1}>
                    <SmsIcon /> SMS
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Country Code (SMS only) */}
            {newNotification.channel === 'sms' && (
              <FormControl fullWidth>
                <InputLabel>Country Code</InputLabel>
                <Select
                  value={newNotification.countryCode || '+1'}
                  label="Country Code"
                  onChange={(e: any) =>
                    setNewNotification({
                      ...newNotification,
                      countryCode: e.target.value,
                    })
                  }
                >
                  <MenuItem value="+1">+1 (US/Canada)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                  <MenuItem value="+61">+61 (Australia)</MenuItem>
                  <MenuItem value="+49">+49 (Germany)</MenuItem>
                  <MenuItem value="+33">+33 (France)</MenuItem>
                  {/* Add more country codes as needed */}
                </Select>
              </FormControl>
            )}

            {/* Recipient Input */}
            <TextField
              fullWidth
              label={newNotification.channel === 'email' ? 'Email Address' : 'Phone Number'}
              type={newNotification.channel === 'email' ? 'email' : 'tel'}
              value={newNotification.recipient}
              onChange={(e: any) =>
                setNewNotification({
                  ...newNotification,
                  recipient: e.target.value,
                })
              }
              placeholder={
                newNotification.channel === 'email'
                  ? 'manager@company.com'
                  : '5551234567'
              }
              helperText={
                newNotification.channel === 'email'
                  ? 'Enter a valid email address'
                  : 'Enter phone number without country code'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddNotification}
            variant="contained"
            disabled={!newNotification.recipient}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

